import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { formatNumber } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { TrendingUp, TrendingDown, Tv } from "lucide-react";

export default function Rates() {
  const { t, lang } = useLang();
  const [rates, setRates] = useState(null);
  const prevRef = useRef({});
  const loc = useLocation();

  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get("/rates");
        if (!alive) return;
        const newPrev = { ...prevRef.current };
        [...(data.currencies || []), ...(data.gold || []), ...(data.silver || [])].forEach((r) => {
          const key = r.code || r.key;
          newPrev[key] = { buy: r.display_buy, sell: r.display_sell };
        });
        prevRef.current = newPrev;
        setRates(data);
      } catch {}
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 10000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Scroll to anchor (e.g. /rates#gram_altin)
  useEffect(() => {
    if (rates && loc.hash) {
      const id = loc.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#C8A96A]");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#C8A96A]"), 2400);
      }
    }
  }, [rates, loc.hash]);

  if (!rates) return <div className="px-6 py-20 text-center text-sm">{t("common.loading")}</div>;

  return (
    <div data-testid="rates-page" className="px-6 lg:px-16 py-12 max-w-[1440px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#C8A96A] mb-3">{lang === "tr" ? "Canlı Veri" : "Live Data"}</p>
          <h1 className="font-heading text-4xl lg:text-5xl">{t("rates.title")}</h1>
          <p className="text-sm text-[#52525B] mt-3">{t("rates.sub")}</p>
        </div>
        <Link to="/tv" data-testid="open-tv-mode" target="_blank" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#C8A96A] text-white px-6 py-3 text-xs uppercase tracking-[0.25em] transition-colors">
          <Tv size={14} /> {t("nav.tv")}
        </Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <RateTable testid="currency-table" title={t("rates.currency")} items={rates.currencies} prevRef={prevRef} keyField="code" />
        <RateTable testid="gold-table" title={t("rates.gold")} items={rates.gold} prevRef={prevRef} keyField="key" />
        <RateTable testid="silver-table" title={t("rates.silver")} items={rates.silver} prevRef={prevRef} keyField="key" />
      </div>
    </div>
  );
}

function RateTable({ testid, title, items, prevRef, keyField }) {
  const { lang, t } = useLang();
  return (
    <div data-testid={testid} className="bg-white border border-border">
      <div className="px-5 py-4 border-b border-border bg-[#0A0A0A] text-white">
        <h2 className="font-heading text-xl">{title}</h2>
      </div>
      <div className="divide-y divide-border">
        <div className="grid grid-cols-3 px-5 py-2 text-[10px] uppercase tracking-[0.2em] text-[#52525B] bg-[#F5F2EC]">
          <span>{lang === "tr" ? "Ad" : "Name"}</span>
          <span className="text-right">{t("rates.buy")}</span>
          <span className="text-right">{t("rates.sell")}</span>
        </div>
        {(items || []).map((r) => {
          const key = r[keyField];
          const prev = prevRef.current[key] || {};
          const sellUp = prev.sell != null && r.display_sell > prev.sell;
          const sellDown = prev.sell != null && r.display_sell < prev.sell;
          return (
            <div key={key} id={String(key)} data-testid={`rate-row-${key}`} className={`grid grid-cols-3 px-5 py-3 text-sm items-center scroll-mt-32 ${sellUp ? "animate-flash-up" : sellDown ? "animate-flash-down" : ""}`}>
              <span className="truncate">{lang === "tr" ? (r.name_tr || r.name || r.code) : (r.name_en || r.name || r.code)}</span>
              <span className="font-finance text-right">{formatNumber(r.display_buy, 4)}</span>
              <span className="font-finance text-right flex items-center justify-end gap-2">
                {formatNumber(r.display_sell, 4)}
                {sellUp && <TrendingUp size={12} className="text-[#10B981]" />}
                {sellDown && <TrendingDown size={12} className="text-[#EF4444]" />}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
